import { useEffect, useState } from 'react';
import { getCurrentUser, updateUserAttributes } from 'aws-amplify/auth';
import { SessionManagerService } from 'session-manager/services/SessionManagerService';

import { Button } from '@client:common/button';
import { AuthenticatedAccount } from '@client:common/AuthenticatedAccount';
import { Notification } from '@client:common/overlay/notification';
import { GlobalStore } from '@client:common/global-store';
import './AccountForm.dark.scss';
import './AccountForm.light.scss';

interface Props {
  userData: AuthenticatedAccount;
}

export interface UpdateAccountDto {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organization: string;
}

export const AccountForm = (props: Props) => {
  const sessionManagerService = SessionManagerService.getInstance();
  const globalStore = GlobalStore.getInstance();
  const { userData } = props;
  const { email, firstName, lastName, organization } = userData || {};

  const [form, setForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    organization: ''
  });

  useEffect(() => {
    if (userData) {
      setForm({
        email,
        firstName,
        lastName,
        organization
      });
    }
  }, [email, firstName, lastName, organization, userData]);

  async function updateUserData() {
    try {
      // Get the current authenticated user - this is just to verify the user is authenticated
      await getCurrentUser();

      // Update user attributes using the new API
      await updateUserAttributes({
        userAttributes: {
          'custom:firstName': form.firstName,
          'custom:lastName': form.lastName,
          'custom:organization': form.organization
        }
      });

      Notification.open('User-info has been updated.');

      // Restore session to get updated user data
      const authenticatedAccount = await sessionManagerService.restoreSession();
      globalStore.update({
        authenticatedAccount
      });

    } catch (err) {
      console.warn(err);
      Notification.open('Failed to update user information.');
    }
  }

  const onSaveChanges = () => {
    updateUserData();
  };

  return (
    <div className='login-form-container'>
      <h2>Account Details</h2>
      <form className='login-form'>
        <div className='name-field-individual'>
          <label className='form-label disabled' htmlFor="email">Email:</label>
          <input
            id="email"
            type='email'
            className='form-field short-text-form-field__input-field disabled'
            defaultValue={email ? email : ''}
            onChange={(event: any) => {
              setForm({ ...form, email: event.target.value });
            }}
            disabled
          />
        </div>
        <div className='name-fields'>
          <div className='name-field-individual'>
            <label className='form-label' htmlFor="firstName">First Name:</label>
            <input
              id="firstName"
              type='text'
              className='short-text-form-field__input-field'
              defaultValue={firstName ? firstName : ''}
              onChange={(event: any) => {
                setForm({ ...form, firstName: event.target.value });
              }}
            />
          </div>
          <div className='name-field-individual'>
            <label className='form-label' htmlFor="lastName">Last Name:</label>
            <input
              id="lastName"
              type='text'
              className='short-text-form-field__input-field'
              defaultValue={lastName ? lastName : ''}
              onChange={(event: any) => {
                setForm({ ...form, lastName: event.target.value });
              }}
            />
          </div>
        </div>
        <div className='name-field-individual'>
          <label className='form-label' htmlFor="organization">Organization:</label>
          <input
            id="organization"
            type='text'
            className='short-text-form-field__input-field'
            defaultValue={organization ? organization : ''}
            onChange={(event: any) => {
              setForm({ ...form, organization: event.target.value });
            }}
          />
        </div>
        <div className='account-form-button-wrapper'>
          <Button
            style='raised'
            color='primary'
            onClick={() => onSaveChanges()}
          >
            SAVE CHANGES
          </Button>
        </div>
      </form>
    </div>
  );
};
